import { useCallback, useEffect, useMemo, useState } from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';

import { crmApi, errMsg } from '@crm/api';
import {
  directionsActions,
  directionsSelectors,
  directionsThunks,
} from '@crm/features/directions/directionsSlice';
import Icon from '@shared/common/icon/Icon';
import { UiButton, UiCheckbox, UiInput, UiLinkButton, UiNativeSelect, UiRadio, UiSelect, UiTextarea, UiTimePicker } from '@shared/common/ui';
import { monthEndDateOnly, todayDateOnly } from '@shared/helpers/dates';
import { showNotificationPush } from '@shared/slices/notificationPushSlice';

const DIRECTION_TYPE = {
  REGULAR: 1,
  ONCE: 2,
};

const DIRECTION_TYPE_LABELS = {
  [DIRECTION_TYPE.REGULAR]: 'Регулярное',
  [DIRECTION_TYPE.ONCE]: 'Разовое',
};

const COACH_PAYMENT_TYPE_LABELS = {
  1: 'Фиксированная',
  2: 'Почасовая',
  3: 'За занятие',
  4: 'Процентная',
};

const COACH_PAYMENT_TYPE_OPTIONS = [
  { value: '1', label: 'Фиксированная' },
  { value: '2', label: 'Почасовая' },
  { value: '3', label: 'За занятие' },
  { value: '4', label: 'Процентная' },
];

const WEEK_DAYS = [
  { value: '1', label: 'Понедельник' },
  { value: '2', label: 'Вторник' },
  { value: '3', label: 'Среда' },
  { value: '4', label: 'Четверг' },
  { value: '5', label: 'Пятница' },
  { value: '6', label: 'Суббота' },
  { value: '7', label: 'Воскресенье' },
];

function todayString() {
  return todayDateOnly();
}

function monthEndString(date = new Date()) {
  return monthEndDateOnly(date);
}

function defaultRegularTime() {
  return { timeStart: '10:00', timeEnd: '18:00' };
}

function directionTypeLabel(type) {
  const numericType = Number(type);
  if (DIRECTION_TYPE_LABELS[numericType]) return DIRECTION_TYPE_LABELS[numericType];
  return type == null ? '—' : String(type);
}

function directionCoachLabel(direction) {
  if (Array.isArray(direction.coachNames) && direction.coachNames.length) {
    return direction.coachNames.join(', ');
  }
  return direction.coach || '—';
}

function getSettingItemName(item) {
  const value = item?.value ?? {};
  return value?.name || value?.title || value?.value || item?.key || '—';
}

function coachPaymentTypeLabel(type) {
  return COACH_PAYMENT_TYPE_LABELS[Number(type)] ?? `Тип ${type}`;
}

function coachPaymentAmount(amount, type) {
  if (amount == null || amount === '') return '—';
  return Number(type) === 4 ? `${amount}%` : `${amount} ₽`;
}

function tariffPriceLabel(price) {
  if (price == null || price === '') return '—';
  return `${price} ₽`;
}

function buildRegularScheduleState(items) {
  const state = {};
  for (const item of items ?? []) {
    if (!item?.dayNumber) continue;
    if (!item.hallId) continue;
    const hallKey = item.hallId;
    if (!state[hallKey]) state[hallKey] = {};
    state[hallKey][String(item.dayNumber)] = {
      timeStart: item.timeStart ?? '10:00',
      timeEnd: item.timeEnd ?? '18:00',
    };
  }
  return state;
}

function normalizeRegularScheduleState(state) {
  const normalized = {};
  Object.keys(state ?? {})
    .sort()
    .forEach((hallKey) => {
      const days = state?.[hallKey] ?? {};
      const normalizedDays = {};
      Object.keys(days)
        .sort((a, b) => Number(a) - Number(b))
        .forEach((day) => {
          const item = days[day];
          if (!item?.timeStart || !item?.timeEnd) return;
          normalizedDays[day] = {
            timeStart: item.timeStart,
            timeEnd: item.timeEnd,
          };
        });
      if (Object.keys(normalizedDays).length) normalized[hallKey] = normalizedDays;
    });
  return normalized;
}

function buildScheduleSignature(type, onceDraft, regularState) {
  if (Number(type) === DIRECTION_TYPE.ONCE) {
    return JSON.stringify({
      type: DIRECTION_TYPE.ONCE,
      once: {
        hallId: onceDraft?.hallId ?? '',
        eventDate: onceDraft?.eventDate ?? '',
        timeStart: onceDraft?.timeStart ?? '',
        timeEnd: onceDraft?.timeEnd ?? '',
      },
    });
  }
  return JSON.stringify({
    type: DIRECTION_TYPE.REGULAR,
    regular: normalizeRegularScheduleState(regularState),
  });
}

function buildScheduleItems(type, onceDraft, regularState) {
  if (Number(type) === DIRECTION_TYPE.ONCE) {
    return [{
      hallId: onceDraft.hallId || null,
      eventDate: onceDraft.eventDate,
      timeStart: onceDraft.timeStart,
      timeEnd: onceDraft.timeEnd,
    }];
  }
  const items = [];
  Object.entries(normalizeRegularScheduleState(regularState)).forEach(([hallKey, days]) => {
    Object.entries(days).forEach(([dayNumber, item]) => {
      items.push({
        hallId: hallKey,
        dayNumber: Number(dayNumber),
        timeStart: item.timeStart,
        timeEnd: item.timeEnd,
      });
    });
  });
  return items;
}

function buildScheduleCheckItems(type, onceDraft, regularState) {
  if (Number(type) === DIRECTION_TYPE.ONCE) {
    return [{
      key: 'once',
      hallId: onceDraft.hallId || null,
      eventDate: onceDraft.eventDate,
      timeStart: onceDraft.timeStart,
      timeEnd: onceDraft.timeEnd,
    }].filter(isScheduleCheckItemReady);
  }
  return buildScheduleItems(type, onceDraft, regularState)
    .map((item) => ({
      ...item,
      key: `regular:${item.hallId ?? ''}:${item.dayNumber ?? ''}`,
    }))
    .filter(isScheduleCheckItemReady);
}

function isScheduleCheckItemReady(item) {
  if (!item?.hallId || !item.timeStart || !item.timeEnd) return false;
  if (item.timeStart >= item.timeEnd) return false;
  return Boolean(item.eventDate || item.dayNumber);
}

function formatConflictMessages(conflicts) {
  const groups = new Map();
  for (const conflict of conflicts ?? []) {
    const name = conflict.conflictName || 'Без названия';
    const timeStart = conflict.conflictTimeStart || conflict.timeStart;
    const timeEnd = conflict.conflictTimeEnd || conflict.timeEnd;
    const kind = conflict.source === 'form'
      ? 'текущим расписанием'
      : conflict.source === 'calendar' && !conflict.conflictDirectionId
        ? 'событием'
        : 'расписанием';
    const key = [
      kind,
      conflict.conflictDirectionId || name,
      timeStart,
      timeEnd,
    ].join('|');
    const group = groups.get(key) ?? {
      kind,
      name,
      timeStart,
      timeEnd,
      dateFrom: conflict.conflictDateFrom || conflict.eventDate || null,
      dateTo: conflict.conflictDateTo || conflict.eventDate || null,
    };
    const from = conflict.conflictDateFrom || conflict.eventDate || null;
    const to = conflict.conflictDateTo || conflict.eventDate || null;
    if (from && (!group.dateFrom || from < group.dateFrom)) group.dateFrom = from;
    if (to && (!group.dateTo || to > group.dateTo)) group.dateTo = to;
    groups.set(key, group);
  }
  const messages = [...groups.values()].map((group) => {
    const datePart = group.dateFrom && group.dateTo
      ? group.dateFrom === group.dateTo
        ? `${group.dateFrom}`
        : `с ${group.dateFrom} по ${group.dateTo}`
      : '';
    return [
      `Зал занят ${group.kind} "${group.name}"`,
      datePart,
      `${group.timeStart}-${group.timeEnd}`,
    ].filter(Boolean).join(' ');
  });
  return messages.slice(0, 5);
}

function hallLabel(hallId, halls) {
  if (!hallId) return 'Без зала';
  return halls.find((hall) => hall.id === hallId)?.name ?? hallId;
}

function normalizeTariffCatalog(items) {
  return (items ?? []).map((item) => ({
    key: item.id,
    label: getSettingItemName(item),
  }));
}

async function loadCoachOptions(branchId) {
  const { data } = await crmApi(branchId).get('/managers', {
    params: { employeeType: 'coach', limit: 500 },
  });
  return (data.items ?? []).map((manager) => ({
    value: manager.id,
    label: manager.name ?? manager.login ?? manager.id,
  }));
}

export function DirectionsPage() {
  const { branchId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const list = useSelector(directionsSelectors.selectList(branchId));
  const listStatus = useSelector(directionsSelectors.selectListStatus(branchId));
  const saveStatus = useSelector(directionsSelectors.selectSaveStatus(branchId));
  const saveError = useSelector(directionsSelectors.selectSaveError(branchId));
  const [createOpen, setCreateOpen] = useState(false);
  const [coachOptions, setCoachOptions] = useState([]);
  const [filters, setFilters] = useState({
    q: '',
    coachId: '',
  });

  useEffect(() => {
    if (!branchId) return;
    dispatch(directionsThunks.list({
      branchId,
      params: {
        q: filters.q || undefined,
        coachId: filters.coachId || undefined,
        limit: 500,
      },
    }));
  }, [dispatch, branchId, filters]);

  useEffect(() => {
    if (!branchId) return;
    let alive = true;
    loadCoachOptions(branchId)
      .then((items) => {
        if (alive) setCoachOptions(items);
      })
      .catch(() => {
        if (alive) setCoachOptions([]);
      });
    return () => {
      alive = false;
    };
  }, [branchId]);

  return (
    <section>
      <header className="page-header">
        <h1>Направления</h1>
        <div className="page-header__spacer" />
        <UiButton
          type="button"
          onClick={() => {
            dispatch(directionsActions.resetSave(branchId));
            setCreateOpen(true);
          }}
        >
          Новое направление
        </UiButton>
      </header>

      <DirectionCreateModal
        open={createOpen}
        branchId={branchId}
        saveStatus={saveStatus}
        saveError={saveError}
        onClose={() => {
          setCreateOpen(false);
          dispatch(directionsActions.resetSave(branchId));
        }}
        onCreated={(direction) => {
          setCreateOpen(false);
          dispatch(directionsActions.resetSave(branchId));
          if (direction?.id) navigate(String(direction.id));
        }}
      />

      <div className="form-card form-card--wide" style={{ marginTop: 16 }}>
        <div className="form-grid">
          <UiInput
            label="Поиск по названию"
            value={filters.q}
            onChange={(e) => setFilters((prev) => ({ ...prev, q: e.target.value }))}
            placeholder="Введите название"
          />
          <label className="ui-field">
            <span className="ui-field__label">Педагог</span>
            <UiNativeSelect
              value={filters.coachId}
              onChange={(e) => setFilters((prev) => ({ ...prev, coachId: e.target.value }))}
            >
              <option value="">Все</option>
              {coachOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </UiNativeSelect>
          </label>
        </div>
        <div className="form-actions left">
          <UiButton
            variant="ghost"
            type="button"
            onClick={() => setFilters({ q: '', coachId: '' })}
          >
            Очистить
          </UiButton>
        </div>
      </div>

      <div className="form-card form-card--wide" style={{ marginTop: 16 }}>
        <table className="data-table">
          <thead>
            <tr>
              <th>Название</th>
              <th>Тренер</th>
              <th>Тип</th>
              <th>Возраст</th>
              <th>Статус</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {list.map((direction) => (
              <tr key={direction.id}>
                <td>
                  <div style={{ fontWeight: 500 }}>
                    <Link
                      to={String(direction.id)}
                      className="directions-page__name-link"
                    >
                      {direction.name}
                    </Link>
                  </div>
                  {direction.description ? (
                    <div style={{ color: 'var(--color-muted)', fontSize: 13 }}>
                      {direction.description}
                    </div>
                  ) : null}
                </td>
                <td>{directionCoachLabel(direction)}</td>
                <td>{directionTypeLabel(direction.type)}</td>
                <td>
                  {direction.ageStart || direction.ageEnd
                    ? `${direction.ageStart ?? '...'}–${direction.ageEnd ?? '...'}`
                    : '—'}
                </td>
                <td>
                  {direction.status === 1
                    ? 'Активно'
                    : direction.status === 0
                      ? 'Отключено'
                      : direction.status}
                </td>
                <td style={{ textAlign: 'right' }}>
                  <UiButton
                    variant="ghost"
                    size="sm"
                    type="button"
                    onClick={() => navigate(String(direction.id))}
                  >
                    Открыть
                  </UiButton>
                  <UiButton
                    variant="danger"
                    size="sm"
                    type="button"
                    onClick={() => {
                      if (window.confirm('Отвязать направление от филиала?')) {
                        dispatch(directionsThunks.remove({ branchId, id: direction.id }));
                      }
                    }}
                  >
                    Удалить
                  </UiButton>
                </td>
              </tr>
            ))}
            {!list.length && listStatus !== 'loading' ? (
              <tr>
                <td colSpan={6} style={{ color: 'var(--color-muted)', textAlign: 'center' }}>
                  Пока нет направлений
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export function DirectionDetailPage() {
  const { branchId, directionId } = useParams();
  const [direction, setDirection] = useState(null);
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [coachError, setCoachError] = useState(null);
  const [tariffError, setTariffError] = useState(null);
  const [scheduleError, setScheduleError] = useState(null);
  const [extraLessonError, setExtraLessonError] = useState(null);
  const [coachSaving, setCoachSaving] = useState(false);
  const [tariffSaving, setTariffSaving] = useState(false);
  const [scheduleSaving, setScheduleSaving] = useState(false);
  const [extraLessonSaving, setExtraLessonSaving] = useState(false);
  const [scheduleConflictChecking, setScheduleConflictChecking] = useState(false);
  const [extraLessonConflictChecking, setExtraLessonConflictChecking] = useState(false);
  const [scheduleConflicts, setScheduleConflicts] = useState([]);
  const [extraLessonConflicts, setExtraLessonConflicts] = useState([]);
  const [lastConflictSignature, setLastConflictSignature] = useState('');
  const [conflictModal, setConflictModal] = useState(null);
  const [coachOptions, setCoachOptions] = useState([]);
  const [tariffCatalog, setTariffCatalog] = useState([]);
  const [halls, setHalls] = useState([]);
  const [helperChats, setHelperChats] = useState([]);
  const [coachDraft, setCoachDraft] = useState({
    managerId: '',
    typeCalculation: '3',
    amount: '',
  });
  const [tariffDraft, setTariffDraft] = useState({
    tariffId: '',
    price: '',
  });
  const [selectedHallTab, setSelectedHallTab] = useState('');
  const [regularScheduleState, setRegularScheduleState] = useState({});
  const [onceScheduleDraft, setOnceScheduleDraft] = useState({
    hallId: '',
    eventDate: todayString(),
    timeStart: '10:00',
    timeEnd: '11:00',
  });
  const [initialScheduleSignature, setInitialScheduleSignature] = useState('');
  const [calendarModalOpen, setCalendarModalOpen] = useState(false);
  const [calendarPeriod, setCalendarPeriod] = useState({
    from: todayString(),
    to: monthEndString(),
  });
  const [pendingSaveValues, setPendingSaveValues] = useState(null);
  const [extraLessonOpen, setExtraLessonOpen] = useState(false);
  const [extraLessonDraft, setExtraLessonDraft] = useState({
    hallId: '',
    eventDate: todayString(),
    timeStart: '10:00',
    timeEnd: '11:00',
    coachId: '',
  });

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { isSubmitting },
  } = useForm({
    defaultValues: {
      name: '',
      chatName: '',
      chatAllowMessaging: false,
      telegramChatId: '',
      type: DIRECTION_TYPE.REGULAR,
      status: 1,
      ageStart: '',
      ageEnd: '',
      description: '',
    },
  });

  const watchedType = Number(watch('type'));
  const watchedStatus = Number(watch('status'));
  const watchedTelegramChatId = watch('telegramChatId') ?? '';
  const watchedChatAllowMessaging = Boolean(watch('chatAllowMessaging'));

  const coachNameById = useMemo(
    () => Object.fromEntries(coachOptions.map((option) => [option.value, option.label])),
    [coachOptions],
  );

  const directionTypeOptions = useMemo(
    () => [
      { value: String(DIRECTION_TYPE.REGULAR), label: 'Регулярное' },
      { value: String(DIRECTION_TYPE.ONCE), label: 'Разовое' },
    ],
    [],
  );

  const directionStatusOptions = useMemo(
    () => [
      { value: '1', label: 'Активно' },
      { value: '0', label: 'Отключено' },
    ],
    [],
  );

  const availableCoachOptions = useMemo(() => {
    const used = new Set((direction?.coaches ?? []).map((coach) => coach.managerId));
    return coachOptions
      .filter((option) => !used.has(option.value))
      .map((option) => ({ value: option.value, label: option.label }));
  }, [coachOptions, direction?.coaches]);

  const availableTariffOptions = useMemo(() => {
    const used = new Set((direction?.tariffs ?? []).map((tariff) => tariff.tariffId));
    return tariffCatalog
      .filter((item) => !used.has(item.key))
      .map((item) => ({ value: item.key, label: item.label }));
  }, [tariffCatalog, direction?.tariffs]);

  const scheduleHallOptions = useMemo(
    () => halls.map((hall) => ({ value: hall.id, label: hall.name ?? hall.id })),
    [halls],
  );

  const weekDayOptions = useMemo(() => WEEK_DAYS.map((day) => ({ value: day.value, label: day.label })), []);

  const extraLessonCoachOptions = useMemo(
    () => [
      { value: '', label: 'Без педагога' },
      ...(direction?.coaches ?? []).map((coach) => ({
        value: coach.managerId,
        label: coach.managerName ?? coachNameById[coach.managerId] ?? '—',
      })),
    ],
    [direction?.coaches, coachNameById],
  );

  const hallTabs = useMemo(() => {
    return halls.map((hall) => ({ id: hall.id, name: hall.name ?? hall.id }));
  }, [halls]);

  const normalizedSelectedHallTab = selectedHallTab || hallTabs[0]?.id || '';
  const currentHallSchedule = regularScheduleState[normalizedSelectedHallTab] ?? {};
  const currentScheduleSignature = useMemo(
    () => buildScheduleSignature(watchedType, onceScheduleDraft, regularScheduleState),
    [watchedType, onceScheduleDraft, regularScheduleState],
  );
  const scheduleChanged = currentScheduleSignature !== initialScheduleSignature;
  const scheduleConflictKeys = useMemo(
    () => new Set(scheduleConflicts.map((conflict) => conflict.key).filter(Boolean)),
    [scheduleConflicts],
  );
  const extraLessonHasConflict = extraLessonConflicts.length > 0;

  const requestScheduleConflicts = useCallback(async (items, mode = 'schedule') => {
    if (!items.length) return [];
    const { data } = await crmApi(branchId).post(`/directions/${directionId}/schedule/conflicts`, {
      mode,
      items,
    });
    return data.conflicts ?? [];
  }, [branchId, directionId]);

  const loadDirection = async () => {
    if (!branchId || !directionId) return;
    setStatus('loading');
    setError(null);
    try {
      const { data } = await crmApi(branchId).get(`/directions/${directionId}`);
      const item = data.direction;
      setDirection(item);
      reset({
        name: item.name ?? '',
        chatName: item.chatName ?? '',
        chatAllowMessaging: Boolean(item.chatAllowMessaging),
        telegramChatId: item.telegramChatId != null ? String(item.telegramChatId) : '',
        type: item.type ?? DIRECTION_TYPE.REGULAR,
        status: item.status ?? 1,
        ageStart: item.ageStart ?? '',
        ageEnd: item.ageEnd ?? '',
        description: item.description ?? '',
      });
      const once = (item.schedule ?? []).find((schedule) => schedule.eventDate);
      const nextOnceDraft = {
        hallId: once?.hallId ?? item.hallId ?? '',
        eventDate: once?.eventDate ?? todayString(),
        timeStart: once?.timeStart ?? '10:00',
        timeEnd: once?.timeEnd ?? '11:00',
      };
      const nextRegularState = buildRegularScheduleState((item.schedule ?? []).filter((schedule) => schedule.dayNumber));
      setOnceScheduleDraft(nextOnceDraft);
      setRegularScheduleState(nextRegularState);
      setInitialScheduleSignature(
        buildScheduleSignature(item.type ?? DIRECTION_TYPE.REGULAR, nextOnceDraft, nextRegularState),
      );
      setExtraLessonDraft((prev) => ({
        ...prev,
        hallId: once?.hallId ?? item.hallId ?? '',
        coachId: item.coaches?.[0]?.managerId ?? '',
      }));
      setScheduleError(null);
      setSaveError(null);
      setStatus('idle');
    } catch (e) {
      setError(errMsg(e, 'Не удалось загрузить направление'));
      setStatus('error');
    }
  };

  useEffect(() => {
    loadDirection();
  }, [branchId, directionId]);

  useEffect(() => {
    if (!branchId) return;
    let alive = true;
    Promise.all([
      loadCoachOptions(branchId).catch(() => []),
      crmApi(branchId).get('/settings/tariffs').then(({ data }) => normalizeTariffCatalog(data.items)).catch(() => []),
      crmApi(branchId).get('/halls').then(({ data }) => data.halls ?? []).catch(() => []),
      crmApi(branchId).get('/telegram/helper-chats').then(({ data }) => data.items ?? []).catch(() => []),
    ]).then(([coaches, tariffs, hallsList, chats]) => {
      if (!alive) return;
      setCoachOptions(coaches);
      setTariffCatalog(tariffs);
      setHalls(hallsList);
      setHelperChats(chats);
      setSelectedHallTab((prev) => prev || hallsList[0]?.id || '');
    });
    return () => {
      alive = false;
    };
  }, [branchId]);

  useEffect(() => {
    if (!hallTabs.some((item) => item.id === selectedHallTab)) {
      setSelectedHallTab(hallTabs[0]?.id || '');
    }
  }, [hallTabs, selectedHallTab]);

  useEffect(() => {
    if (!branchId || !directionId || !direction) return undefined;
    const items = buildScheduleCheckItems(watchedType, onceScheduleDraft, regularScheduleState);
    if (!items.length) {
      setScheduleConflicts([]);
      setScheduleConflictChecking(false);
      return undefined;
    }

    let alive = true;
    const timer = window.setTimeout(async () => {
      setScheduleConflictChecking(true);
      try {
        const conflicts = await requestScheduleConflicts(items, 'schedule');
        if (!alive) return;
        setScheduleConflicts(conflicts);
        if (conflicts.length) {
          const signature = JSON.stringify(conflicts.map((item) => [item.key, item.message]));
          if (signature !== lastConflictSignature) {
            setLastConflictSignature(signature);
            setConflictModal({
              type: 'warning',
              title: 'Зал занят',
              messages: formatConflictMessages(conflicts),
            });
          }
        } else {
          setLastConflictSignature('');
        }
      } catch (e) {
        if (alive) setScheduleError(errMsg(e, 'Не удалось проверить занятость зала'));
      } finally {
        if (alive) setScheduleConflictChecking(false);
      }
    }, 350);

    return () => {
      alive = false;
      window.clearTimeout(timer);
    };
  }, [
    branchId,
    directionId,
    direction,
    watchedType,
    onceScheduleDraft,
    regularScheduleState,
    lastConflictSignature,
    requestScheduleConflicts,
  ]);

  useEffect(() => {
    if (!branchId || !directionId || !extraLessonOpen) return undefined;
    const items = [{
      key: 'extra',
      hallId: extraLessonDraft.hallId || null,
      eventDate: extraLessonDraft.eventDate,
      timeStart: extraLessonDraft.timeStart,
      timeEnd: extraLessonDraft.timeEnd,
    }].filter(isScheduleCheckItemReady);
    if (!items.length) {
      setExtraLessonConflicts([]);
      setExtraLessonConflictChecking(false);
      return undefined;
    }

    let alive = true;
    const timer = window.setTimeout(async () => {
      setExtraLessonConflictChecking(true);
      try {
        const conflicts = await requestScheduleConflicts(items, 'event');
        if (!alive) return;
        setExtraLessonConflicts(conflicts);
        if (conflicts.length) {
          setConflictModal({
            type: 'warning',
            title: 'Зал занят',
            messages: formatConflictMessages(conflicts),
          });
        }
      } catch (e) {
        if (alive) setExtraLessonError(errMsg(e, 'Не удалось проверить занятость зала'));
      } finally {
        if (alive) setExtraLessonConflictChecking(false);
      }
    }, 350);

    return () => {
      alive = false;
      window.clearTimeout(timer);
    };
  }, [branchId, directionId, extraLessonOpen, extraLessonDraft, requestScheduleConflicts]);

  const onAddCoach = async () => {
    if (!coachDraft.managerId || coachDraft.amount === '') return;
    setCoachError(null);
    setCoachSaving(true);
    try {
      await crmApi(branchId).post(`/directions/${directionId}/coaches`, {
        managerId: coachDraft.managerId,
        typeCalculation: Number(coachDraft.typeCalculation),
        amount: Number(coachDraft.amount),
      });
      setCoachDraft({ managerId: '', typeCalculation: '3', amount: '' });
      await loadDirection();
    } catch (e) {
      setCoachError(errMsg(e, 'Не удалось добавить педагога'));
    } finally {
      setCoachSaving(false);
    }
  };

  const onRemoveCoach = async (managerId) => {
    setCoachError(null);
    setCoachSaving(true);
    try {
      await crmApi(branchId).delete(`/directions/${directionId}/coaches/${managerId}`);
      await loadDirection();
    } catch (e) {
      setCoachError(errMsg(e, 'Не удалось удалить педагога'));
    } finally {
      setCoachSaving(false);
    }
  };

  const onAddTariff = async () => {
    if (!tariffDraft.tariffId || tariffDraft.price === '') return;
    setTariffError(null);
    setTariffSaving(true);
    try {
      await crmApi(branchId).put(`/directions/${directionId}/tariffs/${tariffDraft.tariffId}`, {
        price: Number(tariffDraft.price),
      });
      setTariffDraft({ tariffId: '', price: '' });
      await loadDirection();
    } catch (e) {
      setTariffError(errMsg(e, 'Не удалось добавить тариф'));
    } finally {
      setTariffSaving(false);
    }
  };

  const onRemoveTariff = async (tariffId) => {
    setTariffError(null);
    setTariffSaving(true);
    try {
      await crmApi(branchId).delete(`/directions/${directionId}/tariffs/${tariffId}`);
      await loadDirection();
    } catch (e) {
      setTariffError(errMsg(e, 'Не удалось удалить тариф'));
    } finally {
      setTariffSaving(false);
    }
  };

  const setRegularScheduleDay = (hallKey, day, checked) => {
    setRegularScheduleState((prev) => {
      const next = { ...(prev ?? {}) };
      const hallState = { ...(next[hallKey] ?? {}) };
      if (checked) {
        hallState[day] = hallState[day] ?? defaultRegularTime();
      } else {
        delete hallState[day];
      }
      if (Object.keys(hallState).length) next[hallKey] = hallState;
      else delete next[hallKey];
      return next;
    });
  };

  const setRegularScheduleTime = (hallKey, day, field, value) => {
    setRegularScheduleState((prev) => ({
      ...(prev ?? {}),
      [hallKey]: {
        ...((prev ?? {})[hallKey] ?? {}),
        [day]: {
          ...(((prev ?? {})[hallKey] ?? {})[day] ?? defaultRegularTime()),
          [field]: value,
        },
      },
    }));
  };

  const ensureScheduleAvailable = async (type) => {
    const items = buildScheduleCheckItems(type, onceScheduleDraft, regularScheduleState);
    if (!items.length) return true;
    const conflicts = await requestScheduleConflicts(items, 'schedule');
    setScheduleConflicts(conflicts);
    if (!conflicts.length) return true;
    setScheduleError('Зал занят в выбранное время');
    setConflictModal({
      type: 'warning',
      title: 'Зал занят',
      messages: formatConflictMessages(conflicts),
    });
    return false;
  };

  const ensureExtraLessonAvailable = async () => {
    const items = [{
      key: 'extra',
      hallId: extraLessonDraft.hallId || null,
      eventDate: extraLessonDraft.eventDate,
      timeStart: extraLessonDraft.timeStart,
      timeEnd: extraLessonDraft.timeEnd,
    }].filter(isScheduleCheckItemReady);
    if (!items.length) return true;
    const conflicts = await requestScheduleConflicts(items, 'event');
    setExtraLessonConflicts(conflicts);
    if (!conflicts.length) return true;
    setExtraLessonError('Зал занят в выбранное время');
    setConflictModal({
      type: 'warning',
      title: 'Зал занят',
      messages: formatConflictMessages(conflicts),
    });
    return false;
  };

  const persistDirection = async (values, fillRange = null) => {
    setSaveError(null);
    setScheduleError(null);
    setScheduleSaving(true);
    try {
      const type = Number(values.type);
      const scheduleItems = buildScheduleItems(type, onceScheduleDraft, regularScheduleState);
      await crmApi(branchId).patch(`/directions/${directionId}`, {
        name: values.name.trim(),
        chatName: values.chatName.trim() || null,
        chatAllowMessaging: Boolean(values.chatAllowMessaging),
        telegramChatId: values.telegramChatId != null && String(values.telegramChatId).trim()
          ? String(values.telegramChatId).trim()
          : null,
        description: values.description.trim() || null,
        type,
        status: Number(values.status),
        ageStart: values.ageStart === '' ? null : Number(values.ageStart),
        ageEnd: values.ageEnd === '' ? null : Number(values.ageEnd),
      });
      await crmApi(branchId).put(`/directions/${directionId}/schedule`, {
        items: scheduleItems,
      });
      if (fillRange && Number(values.status) === 1) {
        await crmApi(branchId).post(`/directions/${directionId}/calendar`, fillRange);
      }
      setCalendarModalOpen(false);
      setPendingSaveValues(null);
      await loadDirection();
    } catch (e) {
      const message = errMsg(e, 'Не удалось сохранить направление');
      setSaveError(message);
      setScheduleError(message);
    } finally {
      setScheduleSaving(false);
    }
  };

  const onSaveBase = async (values) => {
    const type = Number(values.type);
    const scheduleItems = buildScheduleItems(type, onceScheduleDraft, regularScheduleState);

    if (!halls.length) {
      setScheduleError('Для расписания нужно добавить хотя бы один зал');
      return;
    }

    if (type === DIRECTION_TYPE.ONCE) {
      if (!onceScheduleDraft.hallId || !onceScheduleDraft.eventDate || !onceScheduleDraft.timeStart || !onceScheduleDraft.timeEnd) {
        setScheduleError('Заполните зал, дату и время разового занятия');
        return;
      }
      if (scheduleConflictChecking) {
        setScheduleError('Дождитесь проверки занятости зала');
        return;
      }
      if (scheduleConflicts.length) {
        setScheduleError('Зал занят в выбранное время');
        setConflictModal({
          type: 'warning',
          title: 'Зал занят',
          messages: formatConflictMessages(scheduleConflicts),
        });
        return;
      }
      if (!(await ensureScheduleAvailable(type))) {
        setScheduleError('Зал занят в выбранное время');
        return;
      }
      await persistDirection(
        values,
        Number(values.status) === 1
          ? {
              from: todayString(),
              to: onceScheduleDraft.eventDate,
            }
          : null,
      );
      return;
    }

    if (!scheduleItems.length) {
      setScheduleError('Заполните регулярное расписание');
      return;
    }

    if (scheduleConflictChecking) {
      setScheduleError('Дождитесь проверки занятости зала');
      return;
    }

    if (scheduleConflicts.length) {
      setScheduleError('Зал занят в выбранное время');
      setConflictModal({
        type: 'warning',
        title: 'Зал занят',
        messages: formatConflictMessages(scheduleConflicts),
      });
      return;
    }

    if (!(await ensureScheduleAvailable(type))) {
      setScheduleError('Зал занят в выбранное время');
      return;
    }

    if (scheduleChanged && Number(values.status) === 1) {
      setPendingSaveValues(values);
      setCalendarPeriod((prev) => ({
        from: prev.from < todayString() ? todayString() : prev.from || todayString(),
        to: prev.to || monthEndString(),
      }));
      setCalendarModalOpen(true);
      return;
    }

    await persistDirection(values, null);
  };

  const confirmCalendarFill = async () => {
    if (!pendingSaveValues) return;
    if (!calendarPeriod.from || !calendarPeriod.to || calendarPeriod.from > calendarPeriod.to) {
      setScheduleError('Укажите корректный период заполнения календаря');
      return;
    }
    if (scheduleConflictChecking) {
      setScheduleError('Дождитесь проверки занятости зала');
      return;
    }
    if (scheduleConflicts.length) {
      setScheduleError('Зал занят в выбранное время');
      setConflictModal({
        type: 'warning',
        title: 'Зал занят',
        messages: formatConflictMessages(scheduleConflicts),
      });
      return;
    }
    if (!(await ensureScheduleAvailable(Number(pendingSaveValues.type)))) {
      setScheduleError('Зал занят в выбранное время');
      return;
    }
    await persistDirection(pendingSaveValues, calendarPeriod);
  };

  const onCreateExtraLesson = async () => {
    if (!extraLessonDraft.hallId || !extraLessonDraft.eventDate || !extraLessonDraft.timeStart || !extraLessonDraft.timeEnd) {
      setExtraLessonError('Заполните зал, дату и время дополнительного занятия');
      return;
    }
    if (extraLessonConflictChecking) {
      setExtraLessonError('Дождитесь проверки занятости зала');
      return;
    }
    if (extraLessonConflicts.length) {
      setExtraLessonError('Зал занят в выбранное время');
      setConflictModal({
        type: 'warning',
        title: 'Зал занят',
        messages: formatConflictMessages(extraLessonConflicts),
      });
      return;
    }
    if (!(await ensureExtraLessonAvailable())) {
      setExtraLessonError('Зал занят в выбранное время');
      return;
    }
    setExtraLessonError(null);
    setExtraLessonSaving(true);
    try {
      const selectedCoach = (direction?.coaches ?? []).find((item) => item.managerId === extraLessonDraft.coachId);
      await crmApi(branchId).post('/calendar', {
        day: extraLessonDraft.eventDate,
        timeStart: extraLessonDraft.timeStart,
        timeEnd: extraLessonDraft.timeEnd,
        directionId,
        name: direction?.name ?? null,
        type: direction?.type ?? null,
        hallId: extraLessonDraft.hallId || null,
        ageStart: direction?.ageStart ?? null,
        ageEnd: direction?.ageEnd ?? null,
        coachId: extraLessonDraft.coachId || null,
        coachSalaryType: selectedCoach?.typeCalculation ?? null,
        coachSalaryAmount: selectedCoach?.amount ?? null,
        typeRecord: 1,
      });
      setExtraLessonOpen(false);
      setExtraLessonDraft((prev) => ({
        ...prev,
        eventDate: todayString(),
        timeStart: '10:00',
        timeEnd: '11:00',
      }));
    } catch (e) {
      setExtraLessonError(errMsg(e, 'Не удалось создать дополнительное занятие'));
    } finally {
      setExtraLessonSaving(false);
    }
  };

  return (
    <section className="clients-page clients-page--detail">
      <header className="page-header page-header--spread">
        <div>
          <UiLinkButton to="../directions" variant="link" className="clients-page__back">
            ← Направления
          </UiLinkButton>
        </div>
      </header>

      <div className="form-card form-card--wide">
        {status === 'loading' ? <div className="placeholder">Загрузка…</div> : null}
        {error ? <div className="form-error">{error}</div> : null}

        {direction ? (
          <form onSubmit={handleSubmit(onSaveBase)}>
            <div className="clients-page__detail-grid clients-page__detail-grid--profile direction-detail-grid">
              <section className="clients-page__detail-block clients-page__detail-block--main">
                <h3>Основная информация</h3>
                <div className="form-grid">
                  <div className="ui-field" style={{ gridColumn: '1 / -1' }}>
                    <span className="ui-field__label">ID направления для сайта</span>
                    <div className="copy-field">
                      <input
                        className="copy-field__input"
                        type="text"
                        value={direction.id ?? directionId}
                        readOnly
                      />
                      <CopyToClipboard
                        text={direction.id ?? directionId}
                        onCopy={() => dispatch(showNotificationPush({
                          content: 'ID направления скопирован!',
                        }))}
                      >
                        <UiButton type="button" variant="ghost">
                          Скопировать ID
                        </UiButton>
                      </CopyToClipboard>
                    </div>
                    <span className="muted" style={{ marginTop: 6 }}>
                      Передайте этот ID сайту в поле directionIds при создании заявки.
                    </span>
                  </div>
                  <UiInput
                    label="Название"
                    className="field"
                    style={{ gridColumn: '1 / -1' }}
                    {...register('name', { required: true })}
                  />
                  <UiNativeSelect
                    label="Группа в ТГ"
                    className="field"
                    style={{ gridColumn: '1 / -1' }}
                    value={watchedTelegramChatId}
                    onChange={(event) => {
                      setValue('telegramChatId', event.target.value, { shouldDirty: true });
                    }}
                  >
                    <option value="">Не привязана</option>
                    {helperChats.map((chat) => (
                      <option key={chat.id} value={String(chat.chatId ?? '')}>
                        {chat.title}
                      </option>
                    ))}
                  </UiNativeSelect>
                  {helperChats.length === 0 && (
                    <p className="muted" style={{ gridColumn: '1 / -1', margin: 0 }}>
                      Сначала добавьте чаты в Настройках → Telegram-группы
                    </p>
                  )}
                  <UiNativeSelect label="Тип" className="field" {...register('type')}>
                    {directionTypeOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </UiNativeSelect>
                  <UiNativeSelect label="Статус" className="field" {...register('status')}>
                    {directionStatusOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </UiNativeSelect>
                  <div
                    style={{
                      gridColumn: '1 / -1',
                      display: 'grid',
                      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                      gap: 12,
                    }}
                  >
                    <UiInput className="field" label="Возраст от" type="number" {...register('ageStart')} />
                    <UiInput className="field" label="Возраст до" type="number" {...register('ageEnd')} />
                  </div>
                </div>
              </section>

              <section className="clients-page__detail-block clients-page__detail-block--comment direction-detail-grid__description">
                <h3>Описание</h3>
                <UiTextarea className="field" rows={6} {...register('description')} />
              </section>

              <section className="clients-page__detail-block direction-detail-grid__chat">
                <h3>Чат в приложении</h3>
                <p className="muted" style={{ marginTop: 0, marginBottom: 12 }}>
                  Настройки чата направления для мобильного приложения.
                </p>
                <div className="form-grid">
                  <UiInput
                    label="Название чата"
                    className="field"
                    style={{ gridColumn: '1 / -1' }}
                    maxLength={255}
                    {...register('chatName')}
                  />
                  <UiCheckbox
                    label="Разрешить клиентам переписку"
                    checked={watchedChatAllowMessaging}
                    onChange={(e) => setValue('chatAllowMessaging', e.target.checked, { shouldDirty: true })}
                  />
                </div>
              </section>

              <section className="clients-page__detail-block direction-detail-grid__coach">
                <h3>Педагог</h3>
                <div className="form-grid">
                  <UiSelect
                    className="field"
                    label="Педагог"
                    value={coachDraft.managerId}
                    onChange={(value) => setCoachDraft((prev) => ({ ...prev, managerId: value }))}
                    options={availableCoachOptions}
                    placeholder="Выберите педагога"
                  />
                  <UiSelect
                    className="field"
                    label="Тип оплаты"
                    value={coachDraft.typeCalculation}
                    onChange={(value) => setCoachDraft((prev) => ({ ...prev, typeCalculation: value }))}
                    options={COACH_PAYMENT_TYPE_OPTIONS}
                  />
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'minmax(0, 1fr) auto',
                      gap: 8,
                      alignItems: 'end',
                    }}
                  >
                    <UiInput
                      className="field"
                      label="Сумма"
                      type="number"
                      value={coachDraft.amount}
                      onChange={(e) => setCoachDraft((prev) => ({ ...prev, amount: e.target.value }))}
                    />
                    <UiButton
                      type="button"
                      onClick={onAddCoach}
                      disabled={coachSaving || !coachDraft.managerId}
                    >
                      Добавить педагога
                    </UiButton>
                  </div>
                </div>
                {(direction.coaches ?? []).length ? (
                  <ul className="plain-list">
                    {direction.coaches.map((coach) => (
                      <li key={coach.managerId} className="plain-list__item">
                        <span>
                          {coach.managerName ?? coachNameById[coach.managerId] ?? '—'}
                          {' · '}
                          {coachPaymentTypeLabel(coach.typeCalculation)}
                          {' · '}
                          {coachPaymentAmount(coach.amount, coach.typeCalculation)}
                        </span>
                        <UiButton
                          type="button"
                          variant="danger"
                          size="sm"
                          disabled={coachSaving}
                          onClick={() => onRemoveCoach(coach.managerId)}
                          aria-label={`Удалить педагога ${coach.managerName ?? coachNameById[coach.managerId] ?? ''}`}
                          title="Удалить педагога"
                          style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: 8 }}
                        >
                          <Icon icon="Delete" width={18} height={18} />
                        </UiButton>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="clients-page__detail-empty">Педагоги пока не привязаны</div>
                )}
                {coachError ? <div className="form-error">{coachError}</div> : null}
              </section>

              <section className="clients-page__detail-block direction-detail-grid__tariffs">
                <h3>Тарифы</h3>
                <div className="form-grid">
                  <UiSelect
                    className="field"
                    label="Тариф"
                    value={tariffDraft.tariffId}
                    onChange={(value) => setTariffDraft((prev) => ({ ...prev, tariffId: value }))}
                    options={availableTariffOptions}
                    placeholder="Выберите тариф"
                  />
                  <UiInput
                    className="field"
                    label="Стоимость"
                    type="number"
                    value={tariffDraft.price}
                    onChange={(e) => setTariffDraft((prev) => ({ ...prev, price: e.target.value }))}
                  />
                </div>
                <div className="form-actions left">
                  <UiButton type="button" onClick={onAddTariff} disabled={tariffSaving || !tariffDraft.tariffId}>
                    Добавить тариф
                  </UiButton>
                </div>
                {(direction.tariffs ?? []).length ? (
                  <ul className="plain-list">
                    {direction.tariffs.map((tariff) => (
                      <li key={tariff.tariffId} className="plain-list__item">
                        <span style={{ color: 'var(--color-text, #0e0e0f)' }}>
                          {tariff.tariffName ?? tariff.tariffId}
                          {' · '}
                          {tariffPriceLabel(tariff.price)}
                        </span>
                        <UiButton
                          type="button"
                          variant="danger"
                          size="sm"
                          disabled={tariffSaving}
                          onClick={() => onRemoveTariff(tariff.tariffId)}
                          aria-label={`Удалить тариф ${tariff.tariffName ?? tariff.tariffId}`}
                          title="Удалить тариф"
                          style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: 8 }}
                        >
                          <Icon icon="Delete" width={18} height={18} />
                        </UiButton>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="clients-page__detail-empty">Тарифы пока не добавлены</div>
                )}
                {tariffError ? <div className="form-error">{tariffError}</div> : null}
              </section>

              <section className="clients-page__detail-block direction-schedule-block direction-detail-grid__schedule">
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
                  <h3 style={{ margin: 0 }}>Управление расписанием</h3>
                  {watchedType === DIRECTION_TYPE.REGULAR && watchedStatus === 1 ? (
                    <UiButton type="button" variant="ghost" onClick={() => setExtraLessonOpen(true)}>
                      Дополнительное занятие
                    </UiButton>
                  ) : null}
                </div>

                <div className="direction-schedule-block__type-switch">
                  <UiRadio
                    name="direction-schedule-type"
                    checked={watchedType === DIRECTION_TYPE.REGULAR}
                    onChange={() => setValue('type', DIRECTION_TYPE.REGULAR, { shouldDirty: true })}
                    label="Регулярно"
                  />
                  <UiRadio
                    name="direction-schedule-type"
                    checked={watchedType === DIRECTION_TYPE.ONCE}
                    onChange={() => setValue('type', DIRECTION_TYPE.ONCE, { shouldDirty: true })}
                    label="Разовое"
                  />
                </div>

                {watchedType === DIRECTION_TYPE.ONCE ? (
                  <div className="form-grid direction-schedule-block__once-grid" style={{ marginTop: 12 }}>
                    <UiSelect
                      className="field"
                      label="Зал"
                      value={onceScheduleDraft.hallId}
                      onChange={(value) => setOnceScheduleDraft((prev) => ({ ...prev, hallId: value }))}
                      options={scheduleHallOptions}
                      placeholder="Выберите из списка"
                      error={scheduleConflictKeys.has('once') ? 'Зал занят' : null}
                    />
                    <UiInput
                      className="field"
                      label="Дата проведения"
                      type="date"
                      min={todayString()}
                      value={onceScheduleDraft.eventDate}
                      onChange={(e) => setOnceScheduleDraft((prev) => ({ ...prev, eventDate: e.target.value }))}
                      inputClassName={scheduleConflictKeys.has('once') ? 'is-invalid' : ''}
                    />
                    <div className="direction-schedule-block__time-row">
                      <UiTimePicker
                        className="field"
                        label="С"
                        value={onceScheduleDraft.timeStart}
                        onChange={(e) => setOnceScheduleDraft((prev) => ({ ...prev, timeStart: e.target.value }))}
                        inputClassName={scheduleConflictKeys.has('once') ? 'is-invalid' : ''}
                      />
                      <UiTimePicker
                        className="field"
                        label="До"
                        value={onceScheduleDraft.timeEnd}
                        onChange={(e) => setOnceScheduleDraft((prev) => ({ ...prev, timeEnd: e.target.value }))}
                        inputClassName={scheduleConflictKeys.has('once') ? 'is-invalid' : ''}
                      />
                    </div>
                  </div>
                ) : (
                  <div style={{ marginTop: 12 }}>
                    {hallTabs.length ? (
                      <>
                        <div className="direction-schedule-block__halls">
                          {hallTabs.map((tab) => (
                            <UiButton
                              key={tab.id}
                              type="button"
                              className="direction-schedule-block__hall-btn"
                              variant={normalizedSelectedHallTab === tab.id ? undefined : 'ghost'}
                              onClick={() => setSelectedHallTab(tab.id)}
                            >
                              {tab.name}
                            </UiButton>
                          ))}
                        </div>
                        <div className="direction-schedule-block__days">
                          {WEEK_DAYS.map((day) => {
                            const enabled = Boolean(currentHallSchedule?.[day.value]);
                            const conflictKey = `regular:${normalizedSelectedHallTab}:${day.value}`;
                            const hasConflict = scheduleConflictKeys.has(conflictKey);
                            return (
                              <div
                                key={day.value}
                                className={`direction-schedule-block__day-row${hasConflict ? ' is-conflict' : ''}`}
                              >
                                <UiCheckbox
                                  className="direction-schedule-block__day-check"
                                  label={day.label}
                                  checked={enabled}
                                  onChange={(e) =>
                                    setRegularScheduleDay(normalizedSelectedHallTab, day.value, e.target.checked)
                                  }
                                />
                                <div className="direction-schedule-block__day-time">
                                  <UiTimePicker
                                    label="С"
                                    aria-label={`${day.label}: начало`}
                                    disabled={!enabled}
                                    value={currentHallSchedule?.[day.value]?.timeStart ?? '10:00'}
                                    onChange={(e) =>
                                      setRegularScheduleTime(normalizedSelectedHallTab, day.value, 'timeStart', e.target.value)
                                    }
                                    inputClassName={hasConflict ? 'is-invalid' : ''}
                                    error={hasConflict ? 'Зал занят' : null}
                                  />
                                </div>
                                <div className="direction-schedule-block__day-time">
                                  <UiTimePicker
                                    label="До"
                                    aria-label={`${day.label}: окончание`}
                                    disabled={!enabled}
                                    value={currentHallSchedule?.[day.value]?.timeEnd ?? '18:00'}
                                    onChange={(e) =>
                                      setRegularScheduleTime(normalizedSelectedHallTab, day.value, 'timeEnd', e.target.value)
                                    }
                                    inputClassName={hasConflict ? 'is-invalid' : ''}
                                  />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </>
                    ) : (
                      <div className="clients-page__detail-empty">
                        Для регулярного расписания сначала добавьте хотя бы один зал
                      </div>
                    )}
                  </div>
                )}

                {scheduleError ? <div className="form-error">{scheduleError}</div> : null}
              </section>

              <section className="clients-page__detail-save">
                {saveError ? <div className="form-error">{saveError}</div> : null}
                <UiButton
                  type="submit"
                  disabled={isSubmitting || scheduleSaving || scheduleConflictChecking || scheduleConflicts.length > 0}
                >
                  {isSubmitting || scheduleSaving ? 'Сохранение…' : 'Сохранить'}
                </UiButton>
              </section>
            </div>
          </form>
        ) : null}
      </div>

      {calendarModalOpen ? (
        <div className="modal-backdrop" role="presentation" onClick={() => setCalendarModalOpen(false)}>
          <div
            className="modal-card direction-schedule-block__period-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="direction-calendar-period-title"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="modal-card__header">
              <h2 id="direction-calendar-period-title">Период занятий</h2>
              <UiButton variant="ghost" size="sm" type="button" onClick={() => setCalendarModalOpen(false)}>
                ×
              </UiButton>
            </header>
            <div className="modal-card__body">
              <div className="form-grid">
                <UiInput
                  className="field"
                  label="Дата начала"
                  type="date"
                  min={todayString()}
                  value={calendarPeriod.from}
                  onChange={(e) => setCalendarPeriod((prev) => ({ ...prev, from: e.target.value }))}
                />
                <UiInput
                  className="field"
                  label="Дата окончания"
                  type="date"
                  min={calendarPeriod.from || todayString()}
                  value={calendarPeriod.to}
                  onChange={(e) => setCalendarPeriod((prev) => ({ ...prev, to: e.target.value }))}
                />
              </div>
              {scheduleError ? <div className="form-error">{scheduleError}</div> : null}
              <div className="modal-card__actions">
                <UiButton type="button" onClick={confirmCalendarFill} disabled={scheduleSaving}>
                  {scheduleSaving ? 'Сохранение…' : 'Сохранить'}
                </UiButton>
                <UiButton variant="ghost" type="button" onClick={() => setCalendarModalOpen(false)}>
                  Отмена
                </UiButton>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {conflictModal ? (
        <div className="modal-backdrop" role="presentation" onClick={() => setConflictModal(null)}>
          <div
            className={`modal-card direction-conflict-modal modal-card--${conflictModal.type ?? 'info'}`}
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="direction-conflict-title"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="modal-card__header">
              <div className="modal-card__title">
                {['warning', 'error'].includes(conflictModal.type) ? (
                  <Icon
                    icon={conflictModal.type === 'error' ? 'Error' : 'Warning'}
                    width={20}
                    height={20}
                    className="modal-card__title-icon"
                  />
                ) : null}
                <h2 id="direction-conflict-title">{conflictModal.title}</h2>
              </div>
              <UiButton variant="ghost" size="sm" type="button" onClick={() => setConflictModal(null)}>
                ×
              </UiButton>
            </header>
            <div className="modal-card__body">
              <p className="direction-conflict-modal__text">
                Выбранный зал уже занят в это время. Измените зал, дату или время.
              </p>
              {conflictModal.messages?.length ? (
                <ul className="direction-conflict-modal__list">
                  {conflictModal.messages.map((message) => (
                    <li key={message}>{message}</li>
                  ))}
                </ul>
              ) : null}
              <div className="modal-card__actions">
                <UiButton type="button" onClick={() => setConflictModal(null)}>
                  Понятно
                </UiButton>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {extraLessonOpen ? (
        <div className="modal-backdrop" role="presentation" onClick={() => setExtraLessonOpen(false)}>
          <div
            className="modal-card"
            role="dialog"
            aria-modal="true"
            aria-labelledby="direction-extra-lesson-title"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="modal-card__header">
              <h2 id="direction-extra-lesson-title">Дополнительное занятие</h2>
              <UiButton variant="ghost" size="sm" type="button" onClick={() => setExtraLessonOpen(false)}>
                ×
              </UiButton>
            </header>
            <div className="modal-card__body">
              <div className="form-grid">
                <UiSelect
                  className="field"
                  label="Зал"
                  value={extraLessonDraft.hallId}
                  onChange={(value) => setExtraLessonDraft((prev) => ({ ...prev, hallId: value }))}
                  options={scheduleHallOptions}
                  placeholder="Выберите из списка"
                  error={extraLessonHasConflict ? 'Зал занят' : null}
                />
                <UiInput
                  className="field"
                  label="Дата проведения"
                  type="date"
                  min={todayString()}
                  value={extraLessonDraft.eventDate}
                  onChange={(e) => setExtraLessonDraft((prev) => ({ ...prev, eventDate: e.target.value }))}
                  inputClassName={extraLessonHasConflict ? 'is-invalid' : ''}
                />
                <UiTimePicker
                  className="field"
                  label="Время с"
                  value={extraLessonDraft.timeStart}
                  onChange={(e) => setExtraLessonDraft((prev) => ({ ...prev, timeStart: e.target.value }))}
                  inputClassName={extraLessonHasConflict ? 'is-invalid' : ''}
                />
                <UiTimePicker
                  className="field"
                  label="Время по"
                  value={extraLessonDraft.timeEnd}
                  onChange={(e) => setExtraLessonDraft((prev) => ({ ...prev, timeEnd: e.target.value }))}
                  inputClassName={extraLessonHasConflict ? 'is-invalid' : ''}
                />
                {(direction?.coaches ?? []).length ? (
                  <UiSelect
                    className="field"
                    style={{ gridColumn: '1 / -1' }}
                    label="Педагог"
                    value={extraLessonDraft.coachId}
                    onChange={(value) => setExtraLessonDraft((prev) => ({ ...prev, coachId: value }))}
                    options={extraLessonCoachOptions}
                  />
                ) : null}
              </div>
              {extraLessonError ? <div className="form-error">{extraLessonError}</div> : null}
              <div className="modal-card__actions">
                <UiButton
                  type="button"
                  onClick={onCreateExtraLesson}
                  disabled={extraLessonSaving || extraLessonConflictChecking || extraLessonHasConflict}
                >
                  {extraLessonSaving ? 'Создание…' : 'Создать'}
                </UiButton>
                <UiButton variant="ghost" type="button" onClick={() => setExtraLessonOpen(false)}>
                  Отмена
                </UiButton>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}

function DirectionCreateModal({ open, branchId, onClose, onCreated, saveStatus, saveError }) {
  const dispatch = useDispatch();
  const {
    register,
    handleSubmit,
    reset,
  } = useForm({
    defaultValues: {
      name: '',
      type: DIRECTION_TYPE.REGULAR,
      status: 1,
      ageStart: '',
      ageEnd: '',
    },
  });

  useEffect(() => {
    if (!open) return;
    reset({
      name: '',
      type: DIRECTION_TYPE.REGULAR,
      status: 1,
      ageStart: '',
      ageEnd: '',
    });
  }, [open, reset]);

  const onSubmit = async (values) => {
    const payload = {
      name: values.name.trim(),
      type: Number(values.type),
      status: Number(values.status),
      ageStart: values.ageStart === '' ? null : Number(values.ageStart),
      ageEnd: values.ageEnd === '' ? null : Number(values.ageEnd),
    };
    const res = await dispatch(directionsThunks.create({ branchId, payload }));
    if (res.meta.requestStatus === 'fulfilled') onCreated(res.payload?.item ?? null);
  };

  if (!open) return null;

  return (
    <div className="modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="modal-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="new-direction-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="modal-card__header">
          <h2 id="new-direction-title">Новое направление</h2>
          <UiButton variant="ghost" size="sm" type="button" onClick={onClose}>
            ×
          </UiButton>
        </header>
        <form onSubmit={handleSubmit(onSubmit)} className="modal-card__body">
          <h3 style={{ margin: 0 }}>Основная информация</h3>
          <div className="form-grid">
            <UiInput
              label="Название"
              style={{ gridColumn: '1 / -1' }}
              autoFocus
              {...register('name', { required: true })}
            />
            <label className="ui-field">
              <span className="ui-field__label">Тип</span>
              <UiNativeSelect {...register('type')}>
                <option value={DIRECTION_TYPE.REGULAR}>Регулярное</option>
                <option value={DIRECTION_TYPE.ONCE}>Разовое</option>
              </UiNativeSelect>
            </label>
            <label className="ui-field">
              <span className="ui-field__label">Статус</span>
              <UiNativeSelect {...register('status')}>
                <option value={1}>Активно</option>
                <option value={0}>Отключено</option>
              </UiNativeSelect>
            </label>
            <div
              style={{
                gridColumn: '1 / -1',
                display: 'grid',
                gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                gap: 12,
              }}
            >
              <UiInput label="Возраст от" type="number" {...register('ageStart')} />
              <UiInput label="Возраст до" type="number" {...register('ageEnd')} />
            </div>
          </div>

          {saveError ? <div className="form-error">{saveError}</div> : null}

          <div className="modal-card__actions">
            <UiButton type="submit" disabled={saveStatus === 'loading'}>
              {saveStatus === 'loading' ? 'Создание…' : 'Создать'}
            </UiButton>
            <UiButton variant="ghost" type="button" onClick={onClose}>
              Отмена
            </UiButton>
          </div>
        </form>
      </div>
    </div>
  );
}
